import React, { useState } from 'react';

// Architecture layers — updated to reflect current implementation
const LAYERS = [
  {
    id: 'layer0',
    level: 'Layer 0',
    label: 'Workflow Orchestration',
    color: '#E8F4FD',
    border: '#0969DA',
    icon: '🔀',
    components: [
      { id: 'pipeline-engine',  label: 'LangGraph Pipeline Engine', icon: '⚡', desc: 'Stateful graph: discovery→assessment→recommendation→HITL→generation→testing→security→quality→iac→audit' },
      { id: 'app-queue',        label: 'SSE Event Stream',          icon: '📡', desc: 'Real-time events to all 5 frontend tabs — coordinator, sandbox, wiki, pipeline events' },
      { id: 'sandbox-registry', label: 'Sandbox Execution Registry',icon: '🔲', desc: 'Tracks all active kiro-cli executions with lifecycle, cache hits, retry counts' },
      { id: 'callback-handler', label: 'Pause / Resume Controller', icon: '↩️', desc: 'Manual pause at any stage + HITL gates (decisions + requirements approval)' },
    ],
  },
  {
    id: 'layer1',
    level: 'Layer 1',
    label: 'Stage Coordination',
    color: '#FFF8E1',
    border: '#9A6700',
    icon: '🎯',
    components: [
      { id: 'discovery-coord',      label: 'Discovery Coordinator',      icon: '🔍', desc: 'GitScan + ServiceNow + CMDB → picks sandbox by app language/domain' },
      { id: 'assessment-coord',     label: 'Assessment Coordinator',     icon: '🧠', desc: 'Arch-Assess + Security-Assess + Cost-Assess → each sub-agent uses context-aware sandbox' },
      { id: 'recommendation-coord', label: 'Recommendation Coordinator', icon: '🎯', desc: '6R-Recommend (Retire/Rehost/Replatform/Refactor/Rebuild) → context-aware sandbox' },
      { id: 'generation-coord',     label: 'Generation Coordinator',     icon: '⚙️', desc: 'Spec-Gen + Code-Gen → reads Wiki context, picks codegen sandbox by language' },
      { id: 'sandbox-router',       label: 'Dynamic Sandbox Router',     icon: '🔀', desc: 'resolve_for_context(): assessment_csharp_microfinance → assessment_csharp → assessment → WSL' },
    ],
  },
  {
    id: 'layer2',
    level: 'Layer 2',
    label: 'Wiki Knowledge Graph (CORE)',
    color: '#E8F5E9',
    border: '#1A7F37',
    icon: '📚',
    components: [
      { id: 'knowledge-model', label: 'Knowledge Model',        icon: '🗂️', desc: 'Entity + Concept + Assessment + Synthesis pages — accumulates across all pipeline stages' },
      { id: 'three-layer',     label: 'Three-Layer Pattern',    icon: '📄', desc: 'Raw Sources → Wiki Markdown Pages → Kiro YAML Schema' },
      { id: 'api-layer',       label: 'REST + GraphQL API',     icon: '🔌', desc: 'Ingest / Query / Lint / Semantic Search / Graph / Knowledge Metrics / File Result' },
      { id: 'index-metadata',  label: 'Index & Contradiction',  icon: '🔍', desc: 'Search index + cross-references + contradiction detection + synthesis auto-update' },
    ],
  },
  {
    id: 'layer3',
    level: 'Layer 3',
    label: 'Agent Runtime Layer',
    color: '#F3E5F5',
    border: '#6639BA',
    icon: '🤖',
    components: [
      { id: 'k8s-jobs',         label: 'K8s Agent Jobs (×4)',        icon: '☸️', desc: 'Discovery(1CPU/2Gi/30m) · Assessment(1.5CPU/3Gi/45m) · Recommendation · Generation' },
      { id: 'kiro-sandbox',     label: 'Kiro Sandbox Runtime',       icon: '🔲', desc: 'Initialize→Enrich(Wiki)→Cache check→Execute→Publish→Complete · retry backoff · SSE callbacks' },
      { id: 'sandbox-registry2',label: 'Capability Registry',        icon: '🗺️', desc: 'SANDBOX_{CAPABILITY}_{LANG}_{DOMAIN}_URL — hot-reload, no restart needed' },
      { id: 'sandbox-containers',label: '7 Sandbox Containers',      icon: '📦', desc: 'discovery · assessment · requirements · codegen · testing · security · iac — each independently deployable' },
    ],
  },
  {
    id: 'layer4',
    level: 'Layer 4',
    label: 'Persistence Layer',
    color: '#FBE9E7',
    border: '#CF222E',
    icon: '💾',
    components: [
      { id: 's3-wiki',   label: 'S3 / Filesystem Wiki Store', icon: '🪣', desc: 'Markdown pages per app — local: wiki_store/ · POC/Prod: S3 (WIKI_S3_BUCKET)' },
      { id: 'rds-state', label: 'RDS / SQLite State',         icon: '🗃️', desc: 'LangGraph checkpoints + wiki metadata + agent metrics — local: SQLite · POC/Prod: RDS PostgreSQL' },
      { id: 'redis-cache',label: 'Redis / In-Process Cache',  icon: '⚡', desc: 'Sandbox execution dedup cache — local/POC: in-process dict · Prod: ElastiCache Redis' },
    ],
  },
];

const EXTERNAL = [
  { label: 'Git / GitHub / GitLab',    icon: '📁', color: '#0969DA' },
  { label: 'ServiceNow / CMDB',        icon: '🎫', color: '#9A6700' },
  { label: 'Kiro CLI / API Wrapper',   icon: '🤖', color: '#6639BA' },
  { label: 'Sandbox Containers (×7)',  icon: '📦', color: '#CF222E' },
  { label: 'CloudWatch / S3 / RDS',    icon: '☁️', color: '#1A7F37' },
];

function ComponentPill({ comp, active }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        padding: '6px 10px', borderRadius: 8, fontSize: 11,
        background: hover ? '#fff' : 'rgba(255,255,255,0.6)',
        border: '1px solid rgba(0,0,0,0.08)',
        cursor: 'default', transition: 'all 0.15s',
        boxShadow: hover ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
        position: 'relative',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{comp.icon}</span>
        <span style={{ fontWeight: 700, color: '#1A1F2E' }}>{comp.label}</span>
        {active && (
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#0969DA',
            animation: 'pulse-dot 1.2s ease infinite', marginLeft: 'auto' }} />
        )}
      </div>
      {hover && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, zIndex: 100,
          background: '#1A1F2E', color: '#fff', fontSize: 10, padding: '5px 8px',
          borderRadius: 6, whiteSpace: 'nowrap', marginBottom: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          {comp.desc}
        </div>
      )}
    </div>
  );
}

function LayerCard({ layer, activeComponents }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div style={{
      border: `2px solid ${layer.border}`,
      borderRadius: 10, overflow: 'hidden',
      background: layer.color,
    }}>
      <div
        onClick={() => setCollapsed(c => !c)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', cursor: 'pointer',
          borderBottom: collapsed ? 'none' : `1px solid ${layer.border}44`,
        }}
      >
        <span style={{ fontSize: 16 }}>{layer.icon}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: layer.border,
            textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {layer.level}
          </span>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#1A1F2E', marginLeft: 8 }}>
            {layer.label}
          </span>
        </div>
        <span style={{ color: '#8B949E', fontSize: 12 }}>{collapsed ? '▼' : '▲'}</span>
      </div>
      {!collapsed && (
        <div style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {layer.components.map(comp => (
            <ComponentPill key={comp.id} comp={comp}
              active={activeComponents?.has(comp.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ArchitectureView({ nodeStatuses, sseEvents }) {
  // Determine which components are currently active based on SSE events + node statuses
  const activeComponents = new Set();

  if (nodeStatuses?.discovery === 'active') {
    activeComponents.add('pipeline-engine');
    activeComponents.add('discovery-coord');
    activeComponents.add('sandbox-router');
  }
  if (nodeStatuses?.rationalization === 'active') {
    activeComponents.add('assessment-coord');
    activeComponents.add('sandbox-router');
  }
  if (nodeStatuses?.supervisor === 'active') {
    activeComponents.add('recommendation-coord');
    activeComponents.add('sandbox-router');
  }
  if (nodeStatuses?.requirements === 'active' || nodeStatuses?.coding === 'active') {
    activeComponents.add('generation-coord');
    activeComponents.add('sandbox-router');
  }

  const lastEvt = sseEvents?.[sseEvents.length - 1];
  if (lastEvt?.type === 'sandbox_execute') {
    activeComponents.add('kiro-sandbox');
    activeComponents.add('sandbox-containers');
  }
  if (lastEvt?.type === 'sandbox_cache_hit') {
    activeComponents.add('kiro-sandbox');
  }
  if (lastEvt?.type === 'wiki_updated') {
    activeComponents.add('knowledge-model');
    activeComponents.add('s3-wiki');
    activeComponents.add('index-metadata');
  }
  if (sseEvents?.some(e => e.type === 'sandbox_initialize')) {
    activeComponents.add('sandbox-registry');
    activeComponents.add('sandbox-registry2');
  }

  const anyActive = Object.values(nodeStatuses || {}).includes('active');
  if (anyActive) {
    activeComponents.add('pipeline-engine');
    activeComponents.add('app-queue');
  }

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title-accent">🏛️</span> RAXPS AIMod Platform Architecture
        <span style={{ marginLeft: 8, fontSize: 10, color: '#57606A' }}>
          Blueprint BP-4: Hierarchical Multi-Agent System
        </span>
        {anyActive && (
          <span style={{ marginLeft: 'auto', fontSize: 10, background: '#DDF4FF',
            color: '#0969DA', padding: '1px 8px', borderRadius: 8, fontWeight: 700 }}>
            Pipeline Active
          </span>
        )}
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(1.5); }
        }
      `}</style>

      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {LAYERS.map(layer => (
          <LayerCard key={layer.id} layer={layer} activeComponents={activeComponents} />
        ))}

        {/* External integrations */}
        <div style={{ borderTop: '1px dashed #D0D7DE', paddingTop: 10, marginTop: 4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#8B949E',
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            External Integrations
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {EXTERNAL.map(ext => (
              <div key={ext.label} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 8, fontSize: 11,
                background: '#F6F8FA', border: '1px solid #D0D7DE',
              }}>
                <span>{ext.icon}</span>
                <span style={{ color: ext.color, fontWeight: 600 }}>{ext.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
